<template>
  <div class="kiosk-analytics-page">
    <kiosk-analytics-header
      :date-from.sync="dateFrom"
      :date-to.sync="dateTo"
      :company-id.sync="companyId"
      :loading="loading"
      :sample="isSample"
      @search="fetchAnalytics"
      @download="handleDownload"
    />

    <div v-if="loading && !hasData" class="kiosk-analytics-page__loading">
      <p>Loading kiosk analytics…</p>
    </div>

    <div v-else class="kiosk-analytics-page__content" :class="{ 'is-refreshing': loading }">
      <div
        class="kiosk-analytics-page__kpis"
        :class="{ 'kiosk-analytics-page__kpis--wide': data.kpis.length > 6 }"
      >
        <kiosk-kpi-card
          v-for="kpi in data.kpis"
          :key="kpi.key"
          :title="kpi.title"
          :value="kpi.value"
          :caption="kpi.caption"
          :icon="kpi.icon"
          :icon-color="kpi.iconColor"
          :highlight="kpi.highlight"
        />
      </div>

      <div v-if="data.daily" class="kiosk-analytics-page__trend">
        <kiosk-daily-trend :daily="data.daily" />
      </div>

      <div
        class="kiosk-analytics-page__charts"
        :class="{ 'kiosk-analytics-page__charts--single': !data.hasPaymentSplit }"
      >
        <div class="kiosk-analytics-page__col">
          <kiosk-session-outcomes :totals="data.totals" />
        </div>
        <!-- Shown once the API sends cash/card fields (cashBookings, cardBookings, ...). -->
        <div v-if="data.hasPaymentSplit" class="kiosk-analytics-page__col">
          <kiosk-payment-split :totals="data.totals" />
        </div>
      </div>

      <!-- Shown once the API returns per-kiosk rows (`kiosks` array). -->
      <kiosk-performance-table
        v-if="data.kiosks"
        :kiosks="data.kiosks"
        :show-payments="data.hasPaymentSplit"
        :show-cancelled="data.hasCancelled"
        :show-no-fare="data.hasNoFare"
      />
    </div>
  </div>
</template>

<script>
import { getKioskAnalytics } from "@/services/kioskAnalytics";
import KioskAnalyticsHeader from "./components/KioskAnalyticsHeader.vue";
import KioskKpiCard from "./components/KioskKpiCard.vue";
import KioskDailyTrend from "./components/KioskDailyTrend.vue";
import KioskSessionOutcomes from "./components/KioskSessionOutcomes.vue";
import KioskPerformanceTable from "./components/KioskPerformanceTable.vue";
import KioskPaymentSplit from "./components/KioskPaymentSplit.vue";
import {
  buildSampleKioskAnalytics,
  emptyKioskAnalytics,
  getDefaultDateRange,
  normalizeKioskAnalytics,
  toIsoDate,
} from "./kioskAnalyticsHelpers";

// Set VUE_APP_KIOSK_ANALYTICS_SAMPLE=true to always show sample data (design/demo).
const FORCE_SAMPLE = process.env.VUE_APP_KIOSK_ANALYTICS_SAMPLE === "true";

export default {
  name: "KioskAnalytics",
  components: {
    KioskAnalyticsHeader,
    KioskKpiCard,
    KioskDailyTrend,
    KioskSessionOutcomes,
    KioskPerformanceTable,
    KioskPaymentSplit,
  },
  data() {
    const range = getDefaultDateRange();
    return {
      dateFrom: range.from,
      dateTo: range.to,
      companyId: "all",
      loading: false,
      hasData: false,
      isSample: false,
      data: emptyKioskAnalytics(),
      requestId: 0,
    };
  },
  mounted() {
    this.fetchAnalytics();
  },
  methods: {
    useSample() {
      this.data = normalizeKioskAnalytics(
        buildSampleKioskAnalytics(this.dateFrom, this.dateTo)
      );
      this.isSample = true;
      this.hasData = true;
    },
    async fetchAnalytics() {
      if (FORCE_SAMPLE) {
        this.useSample();
        return;
      }

      const requestId = ++this.requestId;
      this.loading = true;

      const payload = {
        startDate: toIsoDate(this.dateFrom),
        endDate: toIsoDate(this.dateTo),
      };
      if (this.companyId && this.companyId !== "all") {
        payload.companyId = this.companyId;
      }

      try {
        const res = await getKioskAnalytics(payload);
        if (requestId !== this.requestId) return;
        this.data = normalizeKioskAnalytics(res);
        this.isSample = false;
        this.hasData = true;
      } catch (error) {
        if (requestId !== this.requestId) return;
        const status = error && error.response && error.response.status;

        if (status === 404 || status === 405) {
          this.$vs.notify({
            title: "Kiosk analytics unavailable",
            text: "The kiosk analytics endpoint was not found on the server (admin/kiosk-analytics).",
            color: "danger",
            iconPack: "feather",
            icon: "icon-alert-circle",
          });
          return;
        }

        const message =
          (error && error.response && error.response.data &&
            (error.response.data.message || error.response.data.responseMessage)) ||
          (error && error.message) ||
          "Failed to fetch kiosk analytics";
        this.$vs.notify({
          title: "Error",
          text: message,
          color: "danger",
          iconPack: "feather",
          icon: "icon-alert-circle",
        });
      } finally {
        if (requestId === this.requestId) this.loading = false;
      }
    },
    escapeCsv(value) {
      const cell = value == null ? "" : String(value);
      return /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
    },
    handleDownload() {
      const t = this.data.totals;
      const rows = [
        ["Metric", "Value"],
        ["Booking Sessions Started", t.sessions],
        ["Self Service Bookings", t.selfServiceBookings],
        ["Video Call Clicks", t.videoCalls],
        ["Completed Bookings", t.completed],
        ...(this.data.hasCancelled ? [["Cancelled Bookings", t.cancelled]] : []),
        ...(this.data.hasNoFare ? [["No Fare Bookings", t.noFare]] : []),
        ["Active Kiosks", t.activeKiosks],
        ["Revenue (GBP)", t.revenue],
      ];
      if (this.data.hasPaymentSplit) rows.push(
        ["Cash Bookings", t.cashBookings],
        ["Card Bookings", t.cardBookings],
        ["Cash Revenue (GBP)", t.cashRevenue],
        ["Card Revenue (GBP)", t.cardRevenue],
      );
      if (this.data.kiosks && this.data.kiosks.length) rows.push([],
        ["Kiosk", "Device ID", "Venue", "Status", "Last Seen", "Sessions", "Self-Service Bookings",
          "Video Call Clicks", "Completed", "Cancelled", "No Fare", "Cash Bookings", "Card Bookings",
          "Cash Revenue (GBP)", "Card Revenue (GBP)", "Conversion %", "Revenue (GBP)"],
        ...this.data.kiosks.map((k) => [
          k.name, k.deviceId, k.venue,
          k.isActive === null ? "" : k.isActive ? "Active" : "Inactive", k.lastActiveAt || "",
          k.sessions, k.selfServiceBookings, k.videoCalls, k.completed,
          this.data.hasCancelled ? k.cancelled : "", this.data.hasNoFare ? k.noFare : "",
          ...(this.data.hasPaymentSplit
            ? [k.cashBookings, k.cardBookings, k.cashRevenue, k.cardRevenue]
            : ["", "", "", ""]),
          k.conversion.toFixed(1), k.revenue,
        ]));
      if (this.data.daily && this.data.daily.length) rows.push([],
        ["Date", "Sessions", "Self-Service Bookings", "Video Calls", "Completed"],
        ...this.data.daily.map((d) => [
          d.date, d.sessions, d.selfServiceBookings, d.videoCalls, d.completed,
        ]));
      const csv = rows.map((r) => r.map((c) => this.escapeCsv(c)).join(",")).join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `kiosk-analytics-${toIsoDate(this.dateFrom)}-${toIsoDate(this.dateTo)}${this.isSample ? "-SAMPLE" : ""}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      this.$vs.notify({
        title: "Download",
        text: "Kiosk analytics CSV exported",
        color: "success",
        iconPack: "feather",
        icon: "icon-download",
      });
    },
  },
};
</script>

<style scoped lang="scss">
.kiosk-analytics-page {
  padding: 0.25rem 0 1.5rem;

  &__loading {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 240px;
    color: #9ca3af;
    font-size: 0.95rem;
  }

  &__content {
    transition: opacity 0.2s ease;

    &.is-refreshing {
      opacity: 0.55;
      pointer-events: none;
    }
  }

  &__kpis {
    display: grid;
    grid-template-columns: repeat(6, minmax(0, 1fr));
    gap: 1rem;
    margin-bottom: 1.5rem;

    // 8 cards (with Cancelled / No Fare) → two rows of 4
    &--wide {
      grid-template-columns: repeat(4, minmax(0, 1fr));
    }

    @media (max-width: 1400px) {
      grid-template-columns: repeat(3, minmax(0, 1fr));

      &--wide {
        grid-template-columns: repeat(4, minmax(0, 1fr));
      }
    }

    @media (max-width: 1100px) {
      &--wide {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 480px) {
      grid-template-columns: 1fr;
    }
  }

  &__trend {
    margin-bottom: 1.5rem;
  }

  &__charts {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 1.5rem;
    margin-bottom: 1.5rem;

    @media (max-width: 1100px) {
      grid-template-columns: 1fr;
    }

    &--single {
      grid-template-columns: minmax(0, 1fr);
    }
  }

  &__col {
    min-width: 0;
  }
}
</style>
